import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs, breadcrumbListJsonLd } from "@/components/site/Breadcrumbs";
import { CTASection } from "@/components/site/CTASection";
import { PageHero } from "@/components/site/PageShell";
import { canonical } from "@/components/site/site-config";

const TITLE = "Florida Roof Insurance & Code Rules | Destination Roofing";
const DESCRIPTION = "Florida roof insurance and code rules for Citrus County homeowners, including the 15-year rule, roof repairs, wind mitigation and re-roofs.";
const PATH = "/florida-roof-insurance-rules";

export const Route = createFileRoute("/florida-roof-insurance-rules")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: canonical(PATH) },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: canonical(PATH) }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify(
        breadcrumbListJsonLd([
          
          { label: "Florida Roof Insurance & Code Rules", href: PATH },
        ]),
      ),
    }],
  }),
  component: FloridaRoofInsuranceRulesPage,
});

function FloridaRoofInsuranceRulesPage() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Florida Roof Insurance & Code Rules", href: PATH }]} />
      <PageHero
        eyebrow="Homeowner Guide"
        title="Florida Roof Insurance & Code Rules for Citrus County Homeowners"
        subtitle="Florida law limits how insurers can treat older roofs, and a 2022 law changed when a damaged roof must be fully replaced."
      />
      <main className="mx-auto max-w-4xl px-4 py-12 md:py-16">
        <article className="prose prose-slate max-w-none">
          <p className="text-lg leading-relaxed">
            Florida law limits how insurers can treat older roofs, and a 2022 law changed when a damaged roof must be fully replaced. Here is what applies to most Citrus County homes and what to do before calling your insurer.
          </p>

          <h2>The 15-year roof rule</h2>
          <p>
            Under <a href="https://www.flsenate.gov/laws/statutes/2022/627.7011" target="_blank" rel="noopener noreferrer">Florida Statute 627.7011</a>(5), an insurer may not refuse to issue or renew a homeowner&apos;s policy solely because the roof is less than 15 years old. For roofs 15 years or older, the homeowner can have the roof inspected by an authorized inspector at their own expense; if the inspection shows at least 5 years of useful life left, the insurer cannot refuse or non-renew the policy solely because of the roof&apos;s age.
          </p>
          <p>Mobile home and other policy types may be treated differently, so confirm with your agent.</p>

          <h2>The old 25% rule and what changed in 2022</h2>
          <p>
            Florida used to require the whole roof to meet current code if more than 25% was repaired or replaced within 12 months. Since 2022, under Florida Statute 553.844(5), if the roof was built, repaired or replaced under the 2007 Florida Building Code or later, only the damaged portion has to meet current code.
          </p>
          <p>Older roofs can still require more than a patch, which is why an inspection with photos comes first.</p>

          <h2>Wind mitigation inspections</h2>
          <p>
            Florida insurers use the Uniform Mitigation Verification Inspection Form (OIR-B1-1802, revised April 2026). It records roof covering, roof deck attachment, roof-to-wall connections, roof shape, secondary water resistance and opening protection.
          </p>
          <p>
            These features can qualify for insurance premium discounts, so a new roof is a good time to get one. Learn about <Link to="/third-nail-wind-mitigation">third nail wind mitigation</Link> and review the <a href="https://floir.gov/consumers/wind-mitigation-resources" target="_blank" rel="noopener noreferrer">Florida wind mitigation resources</a>.
          </p>

          <h2>Secondary water barrier on re-roofs</h2>
          <p>
            Under the Florida Building Code 8th Edition, effective December 31, 2023, a secondary water barrier is required when a home&apos;s roof covering is removed and replaced, with limited exceptions for some low-slope and tile roofs.
          </p>

          <h2>What to do before you call your insurer</h2>
          <ol>
            <li>Get a roof inspection with photos.</li>
            <li>Find out when your roof was installed or permitted.</li>
            <li>Ask your agent what they need.</li>
            <li>Keep all photos and paperwork.</li>
          </ol>

          <h2>Frequently asked questions</h2>
          <h3>Can an insurer refuse a policy solely because a roof is less than 15 years old?</h3>
          <p>No. Under Florida Statute 627.7011(5), an insurer may not refuse to issue or renew a homeowner&apos;s policy solely because the roof is less than 15 years old.</p>
          <h3>What can a homeowner do when a roof is 15 years or older?</h3>
          <p>The homeowner can have the roof inspected by an authorized inspector at their own expense. If it has at least 5 years of useful life left, the insurer cannot refuse or non-renew the policy solely because of the roof&apos;s age.</p>
          <h3>What does a wind mitigation inspection record?</h3>
          <p>It records roof covering, roof deck attachment, roof-to-wall connections, roof shape, secondary water resistance and opening protection.</p>

          <p className="text-sm text-muted-foreground"><strong>Updated September 2026</strong></p>
          <p className="text-sm text-muted-foreground">This page is general information, not legal or insurance advice. Rules can change; check with your insurance agent.</p>
        </article>
      </main>
      <CTASection
        headline="Schedule My Free Roof Inspection"
        sub="Get a roof inspection with photos before you call your insurer."
      />
    </>
  );
}